import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';

import Pagination from 'components/Pagination';
import ApplicationProcessing from './modals/ApplicationProcessing';
import FilterSearch from 'components/FilterSearch';
import CustomSelect from 'components/CustomSelect';
import FilterDropdown from 'components/FilterDropdown';

import { GET, httpOk } from 'helpers/api';
import { formatDate, formatMoney, handleDateChange } from 'helpers/helpers';
import Detail from './modals/Detail';

function Warehouse() {
	const { t } = useTranslation();

	const priorities = [
		{ id: 'high', name: t('high') },
		{ id: 'normal', name: t('normal') },
		{ id: 'low', name: t('low') },
	];

	const statuses = [
		{ id: 'draft', name: t('draft') },
		{ id: 'submitted', name: t('submitted') },
		{ id: 'approved', name: t('approved') },
		{ id: 'rejected', name: t('rejected') },
		{ id: 'closed', name: t('closed') },
	];

	const [dropdown, setDropdown] = useState(false);
	const [purchaseRequestModal, setPurchaseRequestModal] = useState({ show: false, item: null });
	const [detailModal, setDetailModal] = useState({ show: false, item: {} });

	const initFilterData = {
		page: 1,
		page_size: 20,
		search: '',
		status: '',
	};

	const [filterData, setFilterData] = useState(initFilterData);
	const [data, setData] = useState([]);

	const handlePaginate = (args) => {
		setFilterData(prev => ({
			...prev,
			page: args.page + 1,
			page_size: args.page_size
		}));
	};

	const getData = useCallback(async () => {
		const response = await GET('/procurement/requests/procurement/inbox/', filterData);
		if (httpOk(response)) {
			setData(response.data);
		}
	}, [filterData]);

	useEffect(() => {
		getData();
	}, [getData]);

	return (
		<>
			<div className="d-between">
				<div>
					<div className="main-content-title">
						{t('procurement_cabinet')}
					</div>
					<div className="main-content-subtitle">
						{t('procurement_management')}
					</div>
				</div>
			</div>

			<div className="card">
				<div className="d-flex gap-16 w-100">
					<FilterSearch
						value={filterData.search}
						onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
						placeholder={t('search_by_name')}
					/>

					<CustomSelect
						options={statuses}
						value={statuses.find(option => option.id === filterData?.status) || ''}
						onChange={(option) => setFilterData({ ...filterData, status: option?.id })}
						getOptionLabel={(option) => option.name}
						getOptionValue={(option) => option.id}
						placeholder={t('select_status')}
					/>

					<FilterDropdown dropdown={dropdown} setDropdown={setDropdown}>
						<div className="form-group">
							<label htmlFor="priority">{t('priority')}</label>
							<CustomSelect
								options={priorities}
								value={priorities.find(option => option.id === filterData?.priority) || ''}
								onChange={(option) => setFilterData({ ...filterData, priority: option?.id })}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="d-flex gap-8">
							<div className="form-group">
								<label htmlFor="date_from">{t('date_from')}</label>
								<DatePicker
									selected={filterData.date_from ? new Date(filterData.date_from) : null}
									onChange={(date) => setFilterData(handleDateChange(date, 'date_from', filterData))}
									dateFormat="yyyy-MM-dd"
									placeholderText={t('select_date')}
								/>
							</div>
							<div className="form-group">
								<label htmlFor="date_to">{t('date_to')}</label>
								<DatePicker
									selected={filterData.date_to ? new Date(filterData.date_to) : null}
									onChange={(date) => setFilterData(handleDateChange(date, 'date_to', filterData))}
									dateFormat="yyyy-MM-dd"
									placeholderText={t('select_date')}
								/>
							</div>
						</div>
					</FilterDropdown>
				</div>
			</div>

			<div className="card">
				<div className="d-between mb-30">
					<div className="fz16 text-third">
						{t('requests')}
					</div>
				</div>

				<table className="table">
					<thead>
						<tr>
							<th style={{ width: '20%' }}>{t('date')}</th>
							<th style={{ width: '20%' }}>{t('budget')}</th>
							<th style={{ width: '20%' }}>{t('priority')}</th>
							<th style={{ width: '20%' }}>{t('status')}</th>
							<th style={{ width: '25%' }}>{t('actions')}</th>
						</tr>
					</thead>

					<tbody>
						{data?.results?.map((item, index) => (
							<tr key={index}>
								<td>{formatDate(item.created_at)}</td>
								<td>{formatMoney(item.total_cost)}</td>
								<td>
									<div className={`alert alert-${item.priority} w-fit-content`}>
										{t(item.priority)}
									</div>
								</td>
								<td>
									<div className={`alert alert-${item.status} w-fit-content`}>
										{t(item.status)}
									</div>
								</td>
								<td>
									<div className="d-flex align-items-center gap-8">
										<button className="transparent small" onClick={() => setDetailModal({ show: true, item })}>
											{t('details')}
										</button>
										{item.status === 'submitted' &&
											<button className="dark small" onClick={() => setPurchaseRequestModal({ show: true, item })}>
												{t('process')}
											</button>
										}
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>

				<Pagination
					paginate={handlePaginate}
					pagination={{
						total: data?.count || 0
					}}
				/>
			</div>

			<ApplicationProcessing
				modal={purchaseRequestModal.show}
				setModal={() => setPurchaseRequestModal({ show: false })}
				item={purchaseRequestModal.item}
				getData={getData}
			/>

			<Detail
				modal={detailModal.show}
				setModal={() => setDetailModal({ show: false })}
				item={detailModal.item}
			/>
		</>
	);
}

export default Warehouse;